<template>
    <div
        class="PolicyInfoOtherCellsInfo grid border border-solid border-eidTechGray-25"
        :class="{
            'grid-cols-[20%_15%_15%_15%_35%]': numberOfAccord25Columns === 5,
            'grid-cols-[20%_20%_20%_40%]': numberOfAccord25Columns === 4,
            'grid-cols-[repeat(_auto-fit,_minmax(200px,_1fr))]': numberOfAccord25Columns === 3,
        }"
    >
        <Accord25Cell v-if="typeOfInsurance.length > 0" title="TYPE OF INSURANCE">
            <div
                v-for="attribute in typeOfInsurance"
                :key="attribute.name"
                class="flex flex-col items-start text-eidSecureBlue text-xxs py-1.5 pl-2.5"
                :data-test-id="`PolicyInfoOtherCellsInfo__${attribute.name}TypeOfInsurance`"
            >
                <img
                    v-if="attribute.evaluationError && attribute.evaluationError.isMultiFieldCriterion"
                    class="pr-1.5"
                    :src="foldersIcon"
                >
                <span
                    class="text-left"
                    data-test-id="PolicyInfoOtherCellsInfo__typeOfInsuranceName"
                >
                    {{ formatAttributeName(attribute.name) }}
                </span>
                <span
                    v-for="attributeValueItem in attribute.value"
                    :key="attributeValueItem"
                    data-test-id="PolicyInfoOtherCellsInfo__typeOfInsuranceValue"
                    class="text-eidQualityBlack"
                >
                    {{ attributeValueItem }}
                </span>
                <EvaluationError
                    v-if="attribute.evaluationError && !attribute.evaluationError.isMultiFieldCriterion"
                    class="my-2.5 mx-0"
                    data-test-id="PolicyInfoOtherCellsInfo__typeOfInsuranceError"
                    :evaluation-error="attribute.evaluationError"
                    @go-to-criterion="goToCriterion"
                    @grant-exception-to-criterion="grantExceptionToCriterion"
                    @remove-exceptions="removeExceptions"
                />
            </div>
        </Accord25Cell>
        <Accord25Cell title="POLICY NUMBER">
            {{ requirement.policy.policyNumber }}
            <EvaluationError
                v-if="policyNumberEvaluationError && !policyNumberEvaluationError.isMultiFieldCriterion"
                data-test-id="PolicyInfoOtherCellsInfo__policyNumberError"
                :evaluation-error="policyNumberEvaluationError"
                @go-to-criterion="goToCriterion"
                @grant-exception-to-criterion="grantExceptionToCriterion"
                @remove-exceptions="removeExceptions"
            />
        </Accord25Cell>
        <Accord25Cell title="POLICY EFF">
            {{ requirement.policy.effectiveDate }}
            <EvaluationError
                v-if="effectiveDateEvaluationError && !effectiveDateEvaluationError.isMultiFieldCriterion"
                data-test-id="PolicyInfoOtherCellsInfo__effectiveDateError"
                :evaluation-error="effectiveDateEvaluationError"
                @go-to-criterion="goToCriterion"
                @grant-exception-to-criterion="grantExceptionToCriterion"
                @remove-exceptions="removeExceptions"
            />
        </Accord25Cell>
        <Accord25Cell title=" POLICY EXP">
            {{ requirement.policy.expirationDate }}
            <EvaluationError
                v-if="expirationDateEvaluationError && !expirationDateEvaluationError.isMultiFieldCriterion"
                data-test-id="PolicyInfoOtherCellsInfo__policyExpError"
                :evaluation-error="expirationDateEvaluationError"
                @go-to-criterion="goToCriterion"
                @grant-exception-to-criterion="grantExceptionToCriterion"
                @remove-exceptions="removeExceptions"
            />
        </Accord25Cell>
        <PolicyInfoOtherCellsInfoLimit
            v-if="limits.length > 0"
            :limits="limits"
            @go-to-criterion="goToCriterion"
            @grant-exception-to-criterion="grantExceptionToCriterion"
            @remove-exceptions="removeExceptions"
        />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import startCase from 'lodash/startCase';

    import { EvaluationResult } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { EntityRequirement } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';

    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { FieldEvaluationResultError } from '@/modules/decisioning-criteria/types';
    import foldersIcon from '@/modules/entity-details/assets/folders-icon.svg';
    import Accord25Cell from '@/modules/entity-details/components/Accord25Cell/Accord25Cell.vue';
    import EvaluationError from '@/modules/entity-details/components/EvaluationError/EvaluationError.vue';
    import { FIELD_NOT_AVAILABLE_MESSAGE } from '@/modules/entity-details/constant';
    import { getEnumDefaultDisplayValue } from '@/utils/getEnumDefaultDisplayValue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    import { shouldBeOmitted } from '../PolicyInfo/utils';

    import PolicyInfoOtherCellsInfoLimit from './PolicyInfoOtherCellsInfoLimit/PolicyInfoOtherCellsInfoLimit.vue';
    import { typeOfInsuranceAttributes } from './utils';

    type Limits = {
        name: string;
        value: number | string | unknown;
        evaluationError: FieldEvaluationResultError | null;
    }[];

    export default defineComponent({
        name: 'PolicyInfoOtherCellsInfo',
        components: {
            Accord25Cell,
            EvaluationError,
            PolicyInfoOtherCellsInfoLimit,
        },
        props: {
            requirement: {
                type: Object as PropType<EntityRequirement>,
                required: true,
            },
            evaluationResults: {
                type: Array as PropType<EvaluationResult[]>,
                default: (): EvaluationResult[] => [] as EvaluationResult[],
            },
            evaluationErrors: {
                type: Object as PropType<Record<string, FieldEvaluationResultError>>,
                default: (): Record<string, FieldEvaluationResultError> => (
                    {} as Record<string, FieldEvaluationResultError>
                ),
            },
            limits: {
                type: Array as PropType<Limits>,
                default: (): Limits => [] as Limits,
            },
            getEvaluationResult: {
                type: Function as PropType<(url: string) => FieldEvaluationResultError | null>,
                required: true,
            },
        },
        emits: [ 'go-to-criterion', 'grant-exception-to-criterion', 'remove-exceptions' ],
        data() {
            return {
                typeOfInsuranceAttributes,
                foldersIcon,
            };
        },
        computed: {
            // TODO(PRODUCT-18675): remove any after the migration to the newer version.
            categorizedEnumLabels(): CategorizedEnumLabels {
                return useDashboardStore().categorizedEnumLabels;
            },
            typeOfInsurance(): {
                name: string; value: any;
                evaluationError: FieldEvaluationResultError | null;
            }[] {
                if (!this.requirement) {
                    return [];
                }

                return Object.entries(this.requirement.details)
                    .filter(([ key, value ]) => (
                        typeOfInsuranceAttributes.includes(key)
                        && !shouldBeOmitted(key, value, this.evaluationResults)
                    ))
                    .map(([ key, value ]) => {
                        const _value = value === null
                            ? FIELD_NOT_AVAILABLE_MESSAGE
                            : getEnumDefaultDisplayValue(key, value, this.categorizedEnumLabels);
                        return {
                            name: key,
                            value: Array.isArray(_value) ? _value : [ _value ],
                            evaluationError: this.evaluationErrors[`#/${this.requirement.coverageType}/details/${key}`] || null,
                        };
                    }) || [];
            },
            numberOfAccord25Columns(): number {
                const insuranceTypesColumns = this.typeOfInsurance.length > 0 ? 1 : 0;
                const limitsColumns = this.limits.length > 0 ? 1 : 0;
                const defaultNumberOfColumns = 3;
                return defaultNumberOfColumns + insuranceTypesColumns + limitsColumns;
            },
            expirationDateEvaluationError(): FieldEvaluationResultError | null {
                return this.getEvaluationResult('policy/expirationDate');
            },
            policyNumberEvaluationError(): FieldEvaluationResultError | null {
                return this.getEvaluationResult('policy/policyNumber');
            },
            effectiveDateEvaluationError(): FieldEvaluationResultError | null {
                return this.getEvaluationResult('policy/effectiveDate');
            },
        },
        methods: {
            formatAttributeName(name: string): string {
                return startCase(name);
            },
            goToCriterion(riskProfileId: string): void {
                this.$emit('go-to-criterion', riskProfileId);
            },
            grantExceptionToCriterion(criterionId: string): void {
                this.$emit('grant-exception-to-criterion', criterionId);
            },
            removeExceptions(exceptionIds: string[]): void {
                this.$emit('remove-exceptions', exceptionIds);
            },
        },
    });
</script>
